import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Button,
  Box,
  CircularProgress,
  Paper,
  TextField,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import BulkCreateTeamNames from '../components/BulkCreateTeamNames';
import { useAuth } from '../contexts/AuthContext';
import { useError } from '../contexts/ErrorContext';
import {
  adminEndpoints,
  TeamNameEntry,
  TeamNamesResponse,
  getLanguage,
} from '../services/api';

const TeamNames: React.FC = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { showError } = useError();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [teamNamesData, setTeamNamesData] = useState<TeamNamesResponse | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [createValue, setCreateValue] = useState('');
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [selectedLanguage] = useState<string>(getLanguage());

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/admin/login', { replace: true });
    }
  }, [authLoading, user, navigate]);

  const fetchTeamNames = useCallback(async () => {
    try {
      setLoading(true);
      const response = await adminEndpoints.getTeamNames();
      if (response.data) {
        setTeamNamesData(response.data as TeamNamesResponse);
      } else {
        setTeamNamesData(null);
      }
    } catch (error) {
      console.error('Error fetching team names:', error);
      showError('Failed to fetch team names');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    fetchTeamNames();
  }, [fetchTeamNames, selectedLanguage]);

  const teamNames = teamNamesData?.teamNames ?? [];

  const handleEditClick = (entry: TeamNameEntry) => {
    setEditingId(entry.id);
    setEditValue(entry.teamName);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditValue('');
  };

  const handleUpdate = async (id: string) => {
    if (!editValue.trim()) {
      showError('Team name is required');
      return;
    }

    try {
      setSaving(true);
      await adminEndpoints.updateTeamName(id, { teamName: editValue.trim() });
      setEditingId(null);
      await fetchTeamNames();
    } catch (error) {
      console.error('Error updating team name:', error);
      showError('Failed to update team name');
    } finally {
      setSaving(false);
    }
  };

  const handleCreate = async () => {
    if (!createValue.trim()) {
      showError('Team name is required');
      return;
    }

    try {
      setSaving(true);
      await adminEndpoints.createTeamName({ teamName: createValue.trim() });
      setCreateValue('');
      await fetchTeamNames();
    } catch (error) {
      console.error('Error creating team name:', error);
      showError('Failed to create team name');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this team name?')) {
      return;
    }

    try {
      setSaving(true);
      await adminEndpoints.deleteTeamName(id);
      if (editingId === id) {
        handleCancelEdit();
      }
      await fetchTeamNames();
    } catch (error) {
      console.error('Error deleting team name:', error);
      showError('Failed to delete team name');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const renderTeamNameRow = (entry: TeamNameEntry, index: number) => {
    const isEditing = editingId === entry.id;

    return (
      <Box
        key={entry.id}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          p: 2,
          backgroundColor: index % 2 === 0 ? 'rgba(0, 0, 0, 0.02)' : 'transparent',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        {isEditing ? (
          <TextField
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            size="small"
            fullWidth
            disabled={saving}
            autoFocus
          />
        ) : (
          <Typography variant="body1" sx={{ flexGrow: 1 }}>
            {entry.teamName}
          </Typography>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
          {isEditing ? (
            <>
              <Tooltip title="Done">
                <IconButton
                  size="small"
                  onClick={() => handleUpdate(entry.id)}
                  disabled={saving}
                  sx={{
                    border: '1px solid',
                    borderColor: 'success.main',
                    color: 'success.main',
                  }}
                >
                  <CheckIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Cancel">
                <IconButton
                  size="small"
                  onClick={handleCancelEdit}
                  disabled={saving}
                  sx={{ fontSize: '0.75rem', px: 1 }}
                >
                  ✕
                </IconButton>
              </Tooltip>
            </>
          ) : (
            <>
              <Tooltip title="Edit">
                <IconButton
                  size="small"
                  onClick={() => handleEditClick(entry)}
                  disabled={saving || editingId !== null}
                  sx={{
                    border: '1px solid',
                    borderColor: 'primary.main',
                    color: 'primary.main',
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton
                  size="small"
                  onClick={() => handleDelete(entry.id)}
                  disabled={saving || editingId !== null}
                  sx={{
                    border: '1px solid',
                    borderColor: 'error.main',
                    color: 'error.main',
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
      </Box>
    );
  };

  if (authLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: 2,
          mb: 4,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/admin')}
            sx={{ mr: 2 }}
          >
            Back
          </Button>
          <Typography variant="h4" component="h1">
            Manage Team Names
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="secondary"
          startIcon={<PlaylistAddIcon />}
          onClick={() => setBulkDialogOpen(true)}
          disabled={loading || saving}
        >
          Bulk Create
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Language: {selectedLanguage.toUpperCase()}
        {teamNamesData && (
          <>
            {' · '}
            Last updated: {formatDate(teamNamesData.updatedAt)}
          </>
        )}
      </Typography>

      <Paper>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                p: 2,
                borderBottom: '1px solid',
                borderColor: 'divider',
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              }}
            >
              <Typography variant="subtitle2" sx={{ flexGrow: 1, fontWeight: 'bold' }}>
                Team Name
              </Typography>
              <Box sx={{ width: 88, flexShrink: 0 }} />
            </Box>

            {teamNames.length === 0 ? (
              <Typography color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>
                No team names yet.
              </Typography>
            ) : (
              teamNames.map((entry, index) => renderTeamNameRow(entry, index))
            )}

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                p: 2,
                backgroundColor: 'rgba(25, 118, 210, 0.04)',
                borderTop: '2px solid',
                borderColor: 'primary.main',
              }}
            >
              <TextField
                value={createValue}
                onChange={(e) => setCreateValue(e.target.value)}
                size="small"
                fullWidth
                placeholder="New team name..."
                disabled={saving || editingId !== null}
              />
              <Tooltip title="Done">
                <IconButton
                  size="small"
                  onClick={handleCreate}
                  disabled={saving || editingId !== null || !createValue.trim()}
                  sx={{
                    border: '1px solid',
                    borderColor: 'success.main',
                    color: 'success.main',
                    flexShrink: 0,
                  }}
                >
                  <CheckIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </>
        )}
      </Paper>

      <Dialog
        open={bulkDialogOpen}
        onClose={() => setBulkDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Bulk Create Team Names</DialogTitle>
        <DialogContent>
          <BulkCreateTeamNames
            onSuccess={() => {
              fetchTeamNames();
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TeamNames;
